/**
 * Icon — paleta de ícones SVG inline (D-96, 2026-05-14).
 *
 * Sem dep de lib (zero KB bundle, traceável no codebase).
 * Stroke-based, currentColor, peso 1.75 — suaves e sutis no menu.
 *
 * Uso: <Icon name="mesa" size={16} />
 */
import type { SVGProps } from 'react'

export type IconName =
  | 'home' | 'mesa' | 'reserva' | 'saida' | 'menu' | 'menu-day'
  | 'delivery' | 'pedidos' | 'bebida' | 'marmita'
  | 'historico' | 'vales' | 'edit' | 'equipe' | 'extras'
  | 'logout' | 'bell' | 'search' | 'chevron-down' | 'chevron-right' | 'close'
  | 'hamburger' | 'check' | 'glass'

interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName
  size?: number
}

export default function Icon({ name, size = 16, ...rest }: IconProps) {
  const common = {
    width: size, height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    ...rest,
  }
  switch (name) {
    case 'home':
      return <svg {...common}><path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v10h14V10" /><path d="M10 20v-5h4v5" /></svg>
    case 'mesa':
      return <svg {...common}><rect x="3" y="6" width="18" height="3" rx="1" /><path d="M7 9v11" /><path d="M17 9v11" /></svg>
    case 'reserva':
      return <svg {...common}><rect x="4" y="5" width="16" height="16" rx="2" /><path d="M8 3v4M16 3v4M4 11h16" /></svg>
    case 'saida':
      return <svg {...common}><path d="M14 6V4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-2" /><path d="M21 12H10m0 0 3-3m-3 3 3 3" /></svg>
    case 'menu':
      return <svg {...common}><path d="M4 5h16M4 12h16M4 19h10" /></svg>
    case 'menu-day':
      return <svg {...common}><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18" /><path d="M8 13h8M8 17h5" /></svg>
    case 'delivery':
      return <svg {...common}><path d="M3 7h11l2 4h5v6h-2" /><circle cx="7" cy="17" r="2" /><circle cx="17" cy="17" r="2" /><path d="M3 7v10h2" /></svg>
    case 'pedidos':
      return <svg {...common}><path d="M6 4h12l-1 16H7L6 4Z" /><path d="M9 9h6M9 13h6" /></svg>
    case 'bebida':
      return <svg {...common}><path d="M7 4h10l-1 16H8L7 4Z" /><path d="M7 9h10" /></svg>
    case 'marmita':
      return <svg {...common}><rect x="3" y="8" width="18" height="11" rx="2" /><path d="M3 12h18" /><path d="M8 5h8" /></svg>
    case 'historico':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
    case 'vales':
      return <svg {...common}><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18" /><path d="M7 15h3" /></svg>
    case 'edit':
      return <svg {...common}><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" /></svg>
    case 'equipe':
      return <svg {...common}><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3 3-5 6-5s6 2 6 5" /><circle cx="17" cy="9" r="2.5" /><path d="M15 15c2 0 4 1.5 4 4" /></svg>
    case 'extras':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></svg>
    case 'logout':
      return <svg {...common}><path d="M9 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>
    case 'bell':
      return <svg {...common}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>
    case 'search':
      return <svg {...common}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
    case 'chevron-down':
      return <svg {...common}><path d="m6 9 6 6 6-6" /></svg>
    case 'chevron-right':
      return <svg {...common}><path d="m9 6 6 6-6 6" /></svg>
    case 'close':
      return <svg {...common}><path d="M6 6l12 12M18 6 6 18" /></svg>
    case 'hamburger':
      return <svg {...common}><path d="M4 7h16M4 12h16M4 17h16" /></svg>
    case 'check':
      return <svg {...common}><path d="m5 12 4 4 10-10" /></svg>
    case 'glass':
      // Copo de suco com canudo — D-98 (2026-05-16). Diferenciar de 'bebida' (lata).
      return <svg {...common}><path d="M6 4h12l-1.5 16h-9L6 4Z" /><path d="M7 9h10" /><path d="M14 4v16" /></svg>
    default:
      return null
  }
}
